/**
 * Client for GET /api/cognitory/v2/library/documents/{documentId}/source.
 */

import {
  buildCognitoryLibrarySourcePath,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";
import { openGovernedLibrarySourceBlob } from "../presentation/cognitoryLibrarySourcePresentation";

export { openGovernedLibrarySourceBlob };

export type CognitoryLibrarySourceClientResult =
  | {
      kind: "opened";
      requestId: string | null;
      blob: Blob;
      contentType: string;
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "not_available"
        | "temporary_failure";
      requestId: string | null;
    };

export async function fetchGovernedLibrarySource(input: {
  cognitoryId: string;
  documentId: string;
  documentVersionId?: string;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<CognitoryLibrarySourceClientResult> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const token =
    input.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated", requestId: null };
  }

  const base = input.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  const url = new URL(
    `${base}${buildCognitoryLibrarySourcePath(input.documentId)}`,
  );
  url.searchParams.set("cognitoryId", input.cognitoryId);
  if (input.documentVersionId) {
    url.searchParams.set("documentVersionId", input.documentVersionId);
  }

  let response: Response;
  try {
    response = await fetchImpl(url.toString(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "*/*",
      },
      cache: "no-store",
    });
  } catch {
    return { kind: "temporary_failure", requestId: null };
  }

  const requestId = response.headers.get("X-Cognitory-Request-Id");

  if (response.status === 401) {
    return { kind: "unauthenticated", requestId };
  }
  if (response.status === 403) {
    return { kind: "forbidden", requestId };
  }
  if (response.status === 404) {
    return { kind: "not_available", requestId };
  }
  if (response.status === 503) {
    return { kind: "temporary_failure", requestId };
  }
  if (!response.ok) {
    return { kind: "temporary_failure", requestId };
  }

  const blob = await response.blob();
  const contentType =
    response.headers.get("Content-Type")?.split(";")[0]?.trim() ??
    blob.type ??
    "application/octet-stream";

  return {
    kind: "opened",
    requestId,
    blob,
    contentType,
  };
}
